import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIdeas } from '../hooks/useIdeas';
import IdeaCard from '../components/ideas/IdeaCard';
import { MagnifyingGlassIcon, PlusIcon, LightBulbIcon } from '@heroicons/react/24/outline';

const statusFilters = ['all', 'draft', 'developing', 'ready', 'published', 'archived'];

export default function IdeasPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { ideas, loading } = useIdeas({
    status: activeFilter === 'all' ? undefined : activeFilter,
    search: search || undefined,
  });

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ideas</h1>
        <Link
          to="/capture"
          className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
        >
          <PlusIcon className="w-4 h-4" />
          New
        </Link>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ideas..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-transparent transition-colors"
        />
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {statusFilters.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
              activeFilter === f
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 rounded-full bg-indigo-50 dark:bg-indigo-950 flex items-center justify-center mb-4">
            <LightBulbIcon className="w-8 h-8 text-indigo-400" />
          </div>
          <p className="text-gray-900 dark:text-white font-medium mb-1">No ideas yet</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Your best ideas are waiting to be developed.</p>
          <Link
            to="/capture"
            className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
          >
            Develop your first idea
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {ideas.map((idea) => (
            <IdeaCard key={idea.id} idea={idea} />
          ))}
        </div>
      )}
    </div>
  );
}
